import { FC, useEffect, useRef, useState } from "react";
import CustomButton from "../../UI/Buttons/CustomButton";
import RoundedTick from "../../UI/Icons/RoundedTick";
import Warning from "../../UI/Icons/Warning";
import Copy from "../../UI/Icons/Copy";
import {
  useAppDispatch,
  useAppSelector,
  useAppSelector as useSelector,
} from "../../../store";
import { Link } from "react-router";
import { AUTH_ROUTE } from "../../../pages/App/routes";
import Checkbox from "../../UI/Inputs/Checkbox";
import {
  getCandidateSettingsDataThunk,
  submitCandidateNotificationSettings,
} from "../../../store/candidate/thunks";
import {
  hideTransparentPreloaderAction,
  showNotificationAction,
  showTransparentPreloaderAction,
} from "../../../store/application/slice";
import { useFormik } from "formik";
import { createValidationSchema } from "../../../utils/createValidationSchema";
import { isFulfilled } from "@reduxjs/toolkit";
import { TCandidateSettings } from "../../../store/candidate/types";
import { NOTIFICATION_TIME_OUT } from "../../../constants/app";
import UnsavedChangesModal from "./UnsavedChangesModal";
import { useNavigationPrompt } from "../../../hooks/useNavigationPrompt";

type NotificationType =
  | "scheduled_vacancy"
  | "urgent_vacancy"
  | "centre_cancel_job"
  | "pending_timesheet"
  | "receive_job_confirmation";

type Channel = "sms" | "email" | "daily_summary_email" | "weekly_summary_email";

type Preferences = {
  [key in NotificationType]?: Channel[];
};

const Settings: FC = () => {
  const dispatch = useAppDispatch();

  //Selector declaration
  const user = useSelector((state) => state.auth.user);
  const personalDetails = useSelector(
    (state) => state.onboarding.personaldetails
  );
  const candidateReferralCode = useSelector(
    (state) => state.onboarding.referralCode
  );
  const candidateNotificationSettings = useAppSelector(
    (state) => state.candidateJobs.candidateNotificationSettings
  );
  const inProgress = useAppSelector(
    (state) => state.candidateJobs.saveSettingProgress
  );
  const initialPreferencesRef = useRef<Preferences>({});

  //State declaration
  const [preferences, setPreferences] = useState<Preferences>({});
  const [scheduledVacancyChecked, setScheduledVacancyChecked] = useState(false);
  const [urgentVacancyChecked, setUrgentVacancyChecked] = useState(false);
  const [saveButtonText, setSaveButtonText] = useState<string>("Save Changes");
  const [codeCopied, setCodeCopied] = useState<boolean>(false);
  const [linkCopied, setLinkCopied] = useState<boolean>(false);
  const [showConfirmUnSaved, setShowConfirmUnSaved] = useState(false);
  const [pendingAction, setPendingAction] = useState<string | null>(null);
  const [isDirty, setIsDirty] = useState<boolean>(false)

  // Set initial preferences on first load from DB
  useEffect(() => {
    const savePreferences = convertToPreferences(
      candidateNotificationSettings || []
    );
    initialPreferencesRef.current = savePreferences.preferences;
    // ...existing code...
  }, [candidateNotificationSettings]);

  useEffect(() => {
    // Compare function
    const is_dirty =
      JSON.stringify(preferences) !==
      JSON.stringify(initialPreferencesRef.current);
    setIsDirty(is_dirty)
  }, [preferences, initialPreferencesRef])

  useNavigationPrompt(isDirty, (nextAction) => {
    setPendingAction(nextAction); // nextAction = "back", "forward", "reload", or pathname
    setShowConfirmUnSaved(true);
  });

  /**
   * Handle function to copy the text to clipboard
   * @param type
   * @returns
   */
  const handleCopyText = (type: "codeDiv" | "linkDiv") => {
    const referralCode = document.getElementById(type);

    if (!referralCode) {
      console.error(`Element with id ${type} not found.`);
      return;
    }

    const textToCopy = referralCode.innerText; // Get the text from the div
    navigator.clipboard
      .writeText(textToCopy)
      .then(() => {
        if (type === "codeDiv") {
          setCodeCopied(true);
        } else {
          setLinkCopied(true);
        }

        setTimeout(() => {
          setCodeCopied(false);
          setLinkCopied(false);
        }, 5000);
      })
      .catch((err) => {
        console.error("Failed to copy text: ", err);
      });
  };

  const getTimeZoneInfo = () => {
    // Define the current date
    const now: Date = new Date();

    // Define the options for the formatter
    const options: Intl.DateTimeFormatOptions = {
      timeZoneName: "long",
      timeZone: "Australia/Sydney", // AEDT timezone
    };

    // Create a formatter with the specified options
    const formatter: Intl.DateTimeFormat = new Intl.DateTimeFormat(
      "en-US",
      options
    );

    // Extract formatted timezone details
    const parts: Intl.DateTimeFormatPart[] = formatter.formatToParts(now);

    // Find the part that contains the time zone name
    const timeZoneName: string | undefined = parts.find(
      (part) => part.type === "timeZoneName"
    )?.value;

    // Calculate the offset in hours
    const offsetMinutes: number = -now.getTimezoneOffset();
    const offsetHours: number = offsetMinutes / 60;

    // const timeZone: string = `AEDT (${timeZoneName}) UTC/GMT ${
    //   offsetHours >= 0 ? "+" : ""
    // }${offsetHours} hours`;
    const timeZone: string = `AEDT (Australian Eastern Daylight Time) UTC/GMT ${
      offsetHours >= 0 ? "+" : ""
    }${offsetHours} hours`;
    // Log the timezone information
    return timeZone;
  };

  useEffect(() => {
    dispatch(showTransparentPreloaderAction());
    dispatch(getCandidateSettingsDataThunk());
    dispatch(hideTransparentPreloaderAction());
  }, []);

  // Handle channel checkbox changes for notification preferences
  const handleCheckboxChange = (
    type: NotificationType,
    channel: Channel,
    isChecked: boolean
  ) => {
    // Block adding channels to scheduled_vacancy if parent checkbox is unchecked
    if (type === "scheduled_vacancy" && !scheduledVacancyChecked) return;
    if (type === "urgent_vacancy" && !urgentVacancyChecked) return;

    setPreferences((prev) => {
      const current = prev[type] || [];

      const updated = isChecked
        ? Array.from(new Set([...current, channel]))
        : current.filter((c) => c !== channel);

      const next = {
        ...prev,
        [type]: updated,
      };

      return cleanPreferences(next); // Remove empty arrays
    });
  };

  // Handle scheduled vacancy checkbox change
  const handleScheduledVacancyChange = (isChecked: boolean) => {
    setScheduledVacancyChecked(isChecked);

    setPreferences((prev) => {
      const newPrefs = { ...prev };

      if (isChecked) {
        newPrefs["scheduled_vacancy"] = [];
      } else {
        delete newPrefs["scheduled_vacancy"];
      }

      return newPrefs;
    });
  };

  // Handle urgent vacancy checkbox change
  const handleUrgentVacancyChange = (isChecked: boolean) => {
    setUrgentVacancyChecked(isChecked);

    setPreferences((prev) => {
      const newPrefs = { ...prev };

      if (isChecked) {
        newPrefs["urgent_vacancy"] = [];
      } else {
        delete newPrefs["urgent_vacancy"];
      }

      return newPrefs;
    });
  };

  // Removing empty arrays from preferences
  const cleanPreferences = (prefs: Preferences): Preferences => {
    const cleaned: Preferences = {};
    for (const key in prefs) {
      const arr = prefs[key as NotificationType];
      if (arr && arr.length > 0) {
        cleaned[key as NotificationType] = arr;
      }
    }
    return cleaned;
  };

  // Formik setup for handling form submission
  const {
    handleSubmit,
    setFieldValue,
  } = useFormik<any>({
    validationSchema: createValidationSchema("candidate_save_settings"),
    initialValues: {
      preferences: {} as Preferences,
    },
    onSubmit: async (values: any) => {
      const response = await dispatch(
        submitCandidateNotificationSettings(values)
      );
      if (isFulfilled(response)) {
        dispatch(showNotificationAction("Saved"));
        setIsDirty(false)
        setTimeout(() => {
          setSaveButtonText("Saved!");
        }, NOTIFICATION_TIME_OUT);
      }
    },
  });

  // Converting API response to preferences format
  const convertToPreferences = (
    data: TCandidateSettings[]
  ): { preferences: Preferences } => {
    const preferences: Preferences = {};

    data?.length > 0 &&
      data.forEach(({ notification_type, channel }) => {
        const key = notification_type as NotificationType;
        if (!preferences[key]) {
          preferences[key] = [];
        }
        preferences[key]!.push(channel as Channel);
      });

    return { preferences };
  };

  // Update preferences when they change
  useEffect(() => {
    setFieldValue("preferences", preferences);
    setSaveButtonText("Save Changes");
  }, [preferences]);

  // Initialize preferences from candidateNotificationSettings
  useEffect(() => {
    const savePreferences = convertToPreferences(
      candidateNotificationSettings || []
    );
    setPreferences(savePreferences.preferences);
    setScheduledVacancyChecked(
      !!savePreferences?.preferences?.["scheduled_vacancy"] &&
        savePreferences.preferences["scheduled_vacancy"]!.length > 0
    );
    setUrgentVacancyChecked(
      !!savePreferences?.preferences?.["urgent_vacancy"] &&
        savePreferences.preferences["urgent_vacancy"]!.length > 0
    );
  }, [candidateNotificationSettings]);

 const handleLeave = () => {
  hanldeClose();
  if (!pendingAction) return;

  // Force the previously blocked action
  if (pendingAction === "reload") {
    window.location.reload();
  } else if (pendingAction === "back") {
    window.history.go(-1);
  } else if (pendingAction === "forward") {
    window.history.go(1);
  } else if (pendingAction.startsWith("/")) {
    window.location.href = pendingAction;
  }

  setPendingAction(null);
};

  const handleSave = async () => {
    await handleSubmit();
    handleLeave();
  };

  const hanldeClose = () => {
    setShowConfirmUnSaved(false);
  }

  return (
    <>
      <div className="d-flex flex-column gap-15">
        <div className="h-1px"></div>
        <div className="fs-22 fw-400 text-black">Account Details</div>
        <div className="d-flex flex-column flex-lg-row gap-20">
          <div className="border border-off-white radius-8px p-20px min-w-650px mobile-w-100 d-flex flex-column gap-20">
            <div className="d-flex flex-column flex-lg-row gap-10 justify-content-lg-between">
              <div className="d-flex flex-column gap-5">
                <div className="fs-13 fw-400 text-grey-medium">Email</div>
                <div className="fs-16 fw-400 text-black">{user?.email}</div>
              </div>
              <div className="w-1px h-1px d-none d-lg-block"></div>
              {/* <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-off-white text-black max-h-30px align-self-start align-self-lg-center"
              >
                Edit Details
              </CustomButton> */}
            </div>
            <div className="d-flex flex-column flex-lg-row gap-10 justify-content-lg-between">
              <div className="d-flex flex-column gap-5">
                <div className="fs-13 fw-400 text-grey-medium">Password</div>
                <div className="fs-16 fw-400 text-black">************</div>
              </div>
              {/* <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-off-white text-black max-h-30px align-self-start align-self-lg-center"
              >
                Change Password
              </CustomButton> */}
            </div>
            <div className="d-flex flex-column gap-5">
              <div className="fs-13 fw-400 text-grey-medium">
                Timezone (based on state location)
              </div>
              <div className="fs-16 fw-400 text-black">
                {/* AEDT (Australian Eastern Daylight Time) UTC/GMT +11 hours */}
                {getTimeZoneInfo()}
              </div>
            </div>
          </div>
          <div className="bg-teal-light radius-8px py-10px px-15px align-self-lg-center d-flex gap-6 max-w-240px mobile-w-100">
            <Warning />
            <div className="fs-13 fw-400 text-black">
              You can edit your personal details such as your name and address
              via{" "}
              <span className="fw-700 text-decoration-underline cursor-pointer">
                <Link
                  to={
                    personalDetails?.verification_status === "verified"
                      ? AUTH_ROUTE.MYPROFILE_COMPLETED
                      : AUTH_ROUTE.MY_PROFILE
                  }
                  className="text-black fw-700"
                >
                  My Profile
                </Link>
              </span>
              .
            </div>
          </div>
        </div>
        <div className="h-1px"></div>
        <div className="bg-off-white h-1px w-100"></div>
        <div className="h-1px"></div>
        <div className="d-flex flex-column flex-lg-row gap-20 align-items-lg-center">
          <div className="d-flex flex-column gap-8">
            <div className="fs-22 fw-400 text-black">Refer a Friend</div>
            <div className="fs-16 fw-400 text-black">
              Share your unique code or link with friends! 
              {/* Once each referral completes 38 hours of work through the platform we will email you a Virtual $50 MasterCard or Visa voucher. */}
            </div>
          </div>
          <div className="d-flex flex-column gap-5 position-relative">
            <div className="d-flex gap-5 align-items-lg-center">
              <div
                className="border border-off-white radius-8px px-15px lh-38px text-center"
                id="codeDiv"
              >
                {candidateReferralCode}
              </div>
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-teal text-black text-nowrap"
                onClick={() => handleCopyText("codeDiv")}
              >
                <Copy />
                Copy
              </CustomButton>
            </div>
            {codeCopied && (
              <div className="text-teal-dark position-absolute bottom-25">
                Referral code is copied!
              </div>
            )}
          </div>
          <div className="d-flex flex-column gap-5 position-relative">
            <div className="d-flex gap-5 align-items-center">
              <div
                className="border border-off-white radius-8px px-15px lh-38px text-center text-truncate"
                id="linkDiv"
              >
                {`${process.env.REACT_APP_PUBLIC_URL}/signup?ref=${candidateReferralCode}`}
              </div>
              <CustomButton
                variant={"primary"}
                type="button"
                className="btn-theme bg-teal text-black text-nowrap"
                onClick={() => handleCopyText("linkDiv")}
              >
                <Copy />
                Copy
              </CustomButton>
            </div>
            {linkCopied && (
              <div className="text-teal-dark position-absolute bottom-25">
                Referral link is copied!
              </div>
            )}
          </div>
        </div>
        <div className="h-1px"></div>
        <div className="bg-off-white h-1px w-100"></div>
        <div className="h-1px"></div>
        <div className="d-flex flex-column flex-lg-row gap-20 justify-content-lg-between">
          <div className="d-flex flex-column flex-lg-row gap-10 align-items-lg-center align-self-start">
            <div className="fs-22 fw-400 text-black">Notification Settings</div>
            {/* <div className="d-flex gap-8 align-items-center">
              <svg
                width="20"
                height="20"
                viewBox="0 0 20 20"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M9.16669 5.83268H10.8334V7.49935H9.16669V5.83268ZM9.16669 9.16602H10.8334V14.166H9.16669V9.16602ZM10 1.66602C5.40002 1.66602 1.66669 5.39935 1.66669 9.99935C1.66669 14.5993 5.40002 18.3327 10 18.3327C14.6 18.3327 18.3334 14.5993 18.3334 9.99935C18.3334 5.39935 14.6 1.66602 10 1.66602ZM10 16.666C6.32502 16.666 3.33335 13.6743 3.33335 9.99935C3.33335 6.32435 6.32502 3.33268 10 3.33268C13.675 3.33268 16.6667 6.32435 16.6667 9.99935C16.6667 13.6743 13.675 16.666 10 16.666Z"
                  fill="#757575"
                />
              </svg>

              <div className="fs-13 text-grey-medium">
                Settings and summaries are for all your centres
              </div>
            </div> */}
          </div>
          <CustomButton
            variant={"primary"}
            type="button"
            className="btn-theme bg-teal text-black"
            onClick={handleSubmit}
            isLoading={inProgress}
            disabled={inProgress}
          >
            <RoundedTick />
            {saveButtonText}
          </CustomButton>
        </div>
        <div className="d-flex flex-column gap-10 justify-content-lg-between">
          <div className="fs-16 fw-400 text-black">
            Select which events you want notifications for and how you want to be contacted. Please note, some are mandatory. Daily summaries are sent 5pm your local timezone and weekly summaries are sent every Friday 5pm. If scheduled and urgent daily summaries are selected, this will be delivered as one email.
          </div>
        </div>
        <div className="border border-off-white radius-8px p-20px d-flex flex-column flex-lg-row gap-60 gap-mobile-10">
          <div className="d-flex flex-column gap-8 align-self-end min-w-415px mobile-w-100">
            <div className="fs-16 fw-400 text-black">
              Notify me when a centre matching my preferences posts:
            </div>
            <div className="theme-check">
              <Checkbox
                themeCheck={true}
                className="theme-check-input"
                id="check_01"
                label="A scheduled vacancy"
                labelClass="theme-check-label fs-16 checked-teal"
                labelLeft={false}
                labelFontSize="16"
                checked={scheduledVacancyChecked}
                onChange={(checked) => handleScheduledVacancyChange(checked)}
              />
            </div>
            <div className="theme-check">
              <Checkbox
                themeCheck={true}
                className="theme-check-input"
                id="check_02"
                label="An urgent vacancy (within the next 24 hours)"
                labelClass="theme-check-label fs-16 checked-teal"
                labelLeft={false}
                labelFontSize="16"
                checked={urgentVacancyChecked}
                onChange={(checked) => handleUrgentVacancyChange(checked)}
              />
            </div>
          </div>
          <div className="w-1px bg-grey-light"></div>
          <div className="d-flex justify-content-between w-100 text-center">
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send SMS
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_03"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.scheduled_vacancy?.includes("sms") || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange("scheduled_vacancy", "sms", checked)
                  }
                  disabled={!scheduledVacancyChecked}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_04"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={preferences.urgent_vacancy?.includes("sms") || false}
                  onChange={(checked) =>
                    handleCheckboxChange("urgent_vacancy", "sms", checked)
                  }
                  disabled={!urgentVacancyChecked}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send Email
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_05"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.scheduled_vacancy?.includes("email") || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange("scheduled_vacancy", "email", checked)
                  }
                  disabled={!scheduledVacancyChecked}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_06"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.urgent_vacancy?.includes("email") || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange("urgent_vacancy", "email", checked)
                  }
                  disabled={!urgentVacancyChecked}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Daily Email
                <br />
                Summary
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_07"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.scheduled_vacancy?.includes(
                      "daily_summary_email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "scheduled_vacancy",
                      "daily_summary_email",
                      checked
                    )
                  }
                  disabled={!scheduledVacancyChecked}
                />
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_08"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.urgent_vacancy?.includes(
                      "daily_summary_email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "urgent_vacancy",
                      "daily_summary_email",
                      checked
                    )
                  }
                  disabled={!urgentVacancyChecked}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Weekly Email
                <br />
                Summary
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_09"
                  label=""
                  labelClass="theme-check-label fs-16 checked-teal ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.scheduled_vacancy?.includes(
                      "weekly_summary_email"
                    ) || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange(
                      "scheduled_vacancy",
                      "weekly_summary_email",
                      checked
                    )
                  }
                  disabled={!scheduledVacancyChecked}
                />
              </div>
            </div>
          </div>
        </div>
        <div className="border border-off-white radius-8px p-20px d-flex flex-column flex-lg-row gap-60 gap-mobile-10">
          <div className="d-flex flex-column gap-8 align-self-center min-w-415px mobile-w-100">
            <div className="fs-16 fw-400 text-black">
              Notify me when I receive a job confirmation:
            </div>
          </div>
          <div className="w-1px bg-grey-light"></div>
          <div className="d-flex justify-content-between w-100 text-center">
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send SMS
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_41"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.receive_job_confirmation?.includes("sms") || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange("receive_job_confirmation", "sms", checked)
                  }
                  disabled={true}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send Email
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_42"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.receive_job_confirmation?.includes("email") || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange("receive_job_confirmation", "email", checked)
                  }
                  disabled={true}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8 opacity-0">
              <div className="fs-13 fw-400 text-black">
                Daily Email
                <br />
                Summary
              </div>
            </div>
            <div className="d-flex flex-column gap-8 opacity-0">
              <div className="fs-13 fw-400 text-black">
                Weekly Email
                <br />
                Summary
              </div>
            </div>
          </div>
        </div>
        <div className="border border-off-white radius-8px p-20px d-flex flex-column flex-lg-row gap-60 gap-mobile-10">
          <div className="d-flex flex-column gap-8 align-self-center min-w-415px mobile-w-100">
            <div className="fs-16 fw-400 text-black">
              If a centre cancels a job that I've accepted:
            </div>
          </div>
          <div className="w-1px bg-grey-light"></div>
          <div className="d-flex justify-content-between w-100 text-center">
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send SMS
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_31"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.centre_cancel_job?.includes("sms") || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange("centre_cancel_job", "sms", checked)
                  }
                  disabled={true}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send Email
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_32"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.centre_cancel_job?.includes("email") || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange("centre_cancel_job", "email", checked)
                  }
                  disabled={true}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8 opacity-0">
              <div className="fs-13 fw-400 text-black">
                Daily Email
                <br />
                Summary
              </div>
            </div>
            <div className="d-flex flex-column gap-8 opacity-0">
              <div className="fs-13 fw-400 text-black">
                Weekly Email
                <br />
                Summary
              </div>
            </div>
          </div>
        </div>
        <div className="border border-off-white radius-8px p-20px d-flex flex-column flex-lg-row gap-60 gap-mobile-10">
          <div className="d-flex flex-column gap-8 align-self-center min-w-415px mobile-w-100">
            <div className="fs-16 fw-400 text-black">
              If I have a timesheet to complete:
            </div>
          </div>
          <div className="w-1px bg-grey-light"></div>
          <div className="d-flex justify-content-between w-100 text-center">
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send SMS
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_41"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.pending_timesheet?.includes("sms") || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange("pending_timesheet", "sms", checked)
                  }
                  disabled={true}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8">
              <div className="fs-13 fw-400 text-black">
                Send Email
                <br />
                Immediately
              </div>
              <div className="theme-check">
                <Checkbox
                  themeCheck={true}
                  className="theme-check-input"
                  id="check_42"
                  label=""
                  labelClass="fs-16 checked-grey-light-bg-grey ps-24px"
                  labelLeft={false}
                  labelFontSize="16"
                  checked={
                    preferences.pending_timesheet?.includes("email") || false
                  }
                  onChange={(checked) =>
                    handleCheckboxChange("pending_timesheet", "email", checked)
                  }
                  disabled={true}
                />
              </div>
            </div>
            <div className="d-flex flex-column gap-8 opacity-0">
              <div className="fs-13 fw-400 text-black">
                Daily Email
                <br />
                Summary
              </div>
            </div>
            <div className="d-flex flex-column gap-8 opacity-0">
              <div className="fs-13 fw-400 text-black">
                Weekly Email
                <br />
                Summary
              </div>
            </div>
          </div>
        </div>
      </div>

      <UnsavedChangesModal
        visible={showConfirmUnSaved}
        onLeave={handleLeave}
        onSave={handleSave}
        onClose={hanldeClose}
      />
    </>
  );
};

export default Settings;
